<template>
  <div class="threejsContainer">
    <div class="containerTitle">1-13.fire<button @click="changeMe">火灾层次切换</button></div>
    <div ref="container" class="containerBody"></div>
  </div>
</template>

<script>
import * as THREE from 'three';
import {generateScene} from '@/threejsEnv/scene.js';
import {OrbitControls} from 'three/examples/jsm/controls/OrbitControls.js';
import {genPerspectiveCamera} from '@/threejsEnv/camera.js';
import {genRenderer} from '@/threejsEnv/renderer.js';

export default {
  data() {
    return {
      scene: null,
      camera: null,
      renderer: null,
      controls: null,
      currentMesh: null,
      showLight: false,
      progress: 0,
      cube2: null,
      u_time: 0,
      window: {
        width: 0,
        height: 0
      },
      animationId: null
    };
  },
  mounted() {
    //初始化环境
    this.initEnv();
    //创建粒子系统
    this.createParticleSystem();
    this.createMesh();
    this.animate();
    // 监听窗口大小变化
    window.addEventListener('resize', this.onWindowResize);
  },
  methods: {
    changeMe(){
      this.showLight = !this.showLight;
    },
    createMesh() {
      const geometry2 = new THREE.BoxGeometry();
      const material2 = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
      this.cube2 = new THREE.Mesh(geometry2, material2);
      this.cube2.position.x = 1;
      this.cube2.position.y = 0.5;
      this.cube2.renderOrder = 1; 
      this.scene.add(this.cube2);
    },
    createParticleSystem() {
      // 创建粒子数量
      const particleCount = 500;
      // 创建粒子几何体
      const geometry = new THREE.BufferGeometry();
      // 粒子位置数组
      const positions = new Float32Array(particleCount * 3);
      // 粒子大小数组
      const sizes = new Float32Array(particleCount);
      // 粒子生命周期数组
      const lifetimes = new Float32Array(particleCount);
      // 粒子随机方向数组
      const directions = new Float32Array(particleCount * 3);
      // 初始化粒子
      for (let i = 0; i < particleCount; i++) {
        const i3 = i * 3;
        // 初始位置集中在原点附近
        positions[i3]     = (Math.random() - 0.5) ;
        positions[i3 + 1] = Math.random() * 0.5;
        positions[i3 + 2] = (Math.random() - 0.5);
        // 粒子大小随机
        sizes[i] = 0.3 + Math.random() * 0.20;
        // 生命周期随机
        lifetimes[i] = Math.random();
        // 随机方向（向上为主，带一些左右飘动）
        directions[i3] = (Math.random() - 0.5) * 0.5;
        directions[i3 + 1] = 1 + Math.random() * 0.5; // y方向向上
        directions[i3 + 2] = (Math.random() - 0.5) * 0.5;
      }

      // 设置几何体属性
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
      geometry.setAttribute('lifetime', new THREE.BufferAttribute(lifetimes, 1));
      geometry.setAttribute('direction', new THREE.BufferAttribute(directions, 3));

      // 顶点着色器
      const vertexShader = `
        attribute float size;
        attribute float lifetime;
        attribute vec3 direction;
        uniform float u_time;
        uniform float u_progress;
        varying float v_lifetime;
        
        void main() {
          v_lifetime = lifetime;
          
          // 计算粒子生命周期（0-1）
          float life = fract(lifetime + u_time * 0.3);
          
          // 粒子位置动画（向上运动，带飘动效果）
          vec3 newPosition = position;
          newPosition += direction * life * 2.0;
          
          // 左右飘动效果
          newPosition.x += sin(u_time * 2.0 + lifetime * 10.0) * 0.1 * life;
          newPosition.z += cos(u_time * 2.0 + lifetime * 10.0) * 0.1 * life;
          
          // 投影到裁剪空间
          vec4 mvPosition = modelViewMatrix * vec4(newPosition, 1.0);
          gl_PointSize = size * (1.0 - life) * (300.0 / -mvPosition.z); // 大小随生命周期和距离变化
          gl_Position = projectionMatrix * mvPosition;
        }
      `;

      // 片元着色器
      const fragmentShader = `
        varying float v_lifetime;
        uniform float u_time;
        
        void main() {
          // 计算粒子生命周期（0-1）
          float life = fract(v_lifetime + u_time * 0.3);
          
          // 圆形粒子
          vec2 uv = gl_PointCoord - vec2(0.5);
          float alpha = 1.0 - length(uv) * 2.0;
          
          // 火焰颜色渐变（从黄色到红色再到透明）
          vec3 color;
          if (life < 0.1) {
            color = mix(vec3(1.0, 1.0, 0.0), vec3(1.0, 0.5, 0.0), life / 0.1); // 黄到橙
          } else if (life < 0.2) {
            color = mix(vec3(1.0, 0.5, 0.0), vec3(1.0, 0.0, 0.0), (life - 0.2) / 0.3); // 橙到红
          } else {
            color = mix(vec3(1.0, 0.0, 0.0), vec3(0.0, 0.0, 0.0), (life - 0.3) / 0.5); // 红到暗红
          }
          
          // 透明度随生命周期变化（粒子消失阶段变得透明）
          alpha *= 1.0 - life;
          
          gl_FragColor = vec4(color, alpha);
        }
      `;

      // 创建材质
      const material = new THREE.ShaderMaterial({
        uniforms: {
          u_time: { value: 0 },
          u_progress: { value: this.progress }
        },
        vertexShader,
        fragmentShader,
        transparent: true,
        blending: THREE.AdditiveBlending, //  additive混合让火焰更亮
        depthWrite: false // 关闭深度写入，避免粒子互相遮挡问题
      });

      // 创建粒子系统
      this.particles = new THREE.Points(geometry, material);
      this.particles.renderOrder = 10; 
      this.scene.add(this.particles);

      this.particleMaterial = material;
    },
    animate() {
      this.animationId = requestAnimationFrame(this.animate);
      this.controls.update();

      // 更新时间 uniforms，驱动火焰动画
      if (this.particleMaterial) {
        this.u_time += 0.02; // 近似16ms一帧
        this.particleMaterial.uniforms.u_time.value = this.u_time;
      }
      if(this.showLight){
        this.particles.material.depthTest = false;
      }else{
        this.particles.material.depthTest = true;
      }
      this.renderer.render(this.scene, this.camera);
    },
    initEnv(){
      this.window.width = window.innerWidth;
      this.window.height = window.innerHeight;
      // 初始化场景
      this.scene = generateScene();
      this.scene.background = new THREE.Color(0x000000);

      // 初始化相机
      this.camera = genPerspectiveCamera(this.window);

      // 初始化渲染器
      this.renderer = genRenderer(this.window);
      this.$refs.container.appendChild(this.renderer.domElement);

      // 添加轨道控制器
      this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      this.controls.enableDamping = true;

      // 创建光源
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
      this.scene.add(ambientLight);
      
      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
      directionalLight.position.set(10, 20, 15);
      this.scene.add(directionalLight);

      const gridHelper = new THREE.GridHelper(100, 100,
                                              0x111111,   // 中心轴线颜色（红色）
                                              0x222222    // 普通网格线颜色（绿色）
                                              ); // 10x10 网格
      this.scene.add(gridHelper);
    },
    onWindowResize() {
      const width = this.$refs.container.clientWidth;
      const height = this.$refs.container.clientHeight;

      // 更新相机
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();

      // 更新渲染器
      this.renderer.setSize(width, height);
    }
  },
  beforeDestroy() {
    // 清理动画和事件监听
    cancelAnimationFrame(this.animationId);
    window.removeEventListener('resize', this.onWindowResize);
    
    // 释放资源
    if (this.currentMesh) {
      this.scene.remove(this.currentMesh);
      this.currentMesh.geometry.dispose();
      this.currentMesh.material.dispose();
    }
    
    this.controls.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }
};
</script>

<style>
</style>
